import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';
import '../App.css';

const LegalPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();

  const legalContent = {
    terms: {
      title: "Conditions d'utilisation",
      lastUpdated: "Dernière mise à jour : Février 2026",
      content: (
        <>
          <section className="legal-section">
            <h2>1. Acceptation des conditions</h2>
            <p>
              En accédant et en utilisant SmartBouffe (« le Service »), vous acceptez d'être lié par les présentes
              Conditions d'utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser le Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Description du service</h2>
            <p>
              SmartBouffe est une application web qui permet de convertir des vidéos de recettes et des photos de
              recettes en recettes textuelles structurées à l'aide de l'intelligence artificielle. Le Service offre
              également des fonctionnalités de planification de repas et de gestion de recettes personnalisées.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. Système d'abonnements</h2>
            <h3>3.1 Forfaits mensuels</h3>
            <p>
              SmartBouffe propose des forfaits d'abonnement mensuels avec un nombre maximum de vidéos recettes :
            </p>
            <ul>
              <li>Gratuit : Maximum 5 vidéos recettes/mois (0 $) - <strong>cumulables</strong></li>
              <li>Starter : Maximum 12 vidéos recettes/mois (4,99 $/mois)</li>
              <li>Standard : Maximum 25 vidéos recettes/mois (9,99 $/mois)</li>
              <li>Pro : Maximum 50 vidéos recettes/mois (16,99 $/mois)</li>
              <li>Illimité : Vidéos recettes illimitées (29,99 $/mois)</li>
            </ul>
            <h3>3.2 Non-cumul des vidéos recettes (forfaits payants)</h3>
            <p>
              <strong>IMPORTANT :</strong> Les vidéos recettes des forfaits payants (Starter, Standard, Pro) ne sont <strong>PAS cumulables</strong> d'un mois à l'autre. Les vidéos recettes non utilisées à la fin du cycle mensuel sont perdues et le compteur est réinitialisé.
            </p>
            <h3>3.3 Exception : Forfait Gratuit</h3>
            <p>
              Seul le forfait Gratuit permet de <strong>cumuler</strong> les vidéos recettes non utilisées d'un mois à l'autre, sans limite.
            </p>
            <h3>3.4 Vidéos recettes bonus</h3>
            <p>
              Vous pouvez acheter des vidéos recettes supplémentaires via PayPal. Ces vidéos recettes bonus n'expirent pas et restent disponibles jusqu'à utilisation :
            </p>
            <ul>
              <li>5 vidéos recettes : 2,99 $ CAD</li>
              <li>15 vidéos recettes : 6,99 $ CAD</li>
              <li>30 vidéos recettes : 11,99 $ CAD</li>
            </ul>
            <h3>3.5 Utilisation des vidéos recettes</h3>
            <p>
              Chaque extraction de recette consomme 1 vidéo recette (vidéos de 0 à 3:30 minutes) ou 2 vidéos recettes (vidéos de 3:30 à 5:00 minutes).
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Compte utilisateur</h2>
            <h3>4.1 Création de compte</h3>
            <p>
              Vous devez créer un compte pour utiliser le Service. Vous pouvez vous inscrire par courriel ou via
              Google OAuth. Vous êtes responsable de maintenir la confidentialité de vos informations de connexion.
            </p>
            <h3>4.2 Exactitude des informations</h3>
            <p>
              Vous vous engagez à fournir des informations exactes, complètes et à jour lors de votre inscription et
              à maintenir ces informations à jour.
            </p>
            <h3>4.3 Responsabilité du compte</h3>
            <p>
              Vous êtes entièrement responsable de toutes les activités effectuées sous votre compte. Vous devez nous
              informer immédiatement de toute utilisation non autorisée de votre compte.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Utilisation acceptable</h2>
            <p>Vous acceptez de ne pas :</p>
            <ul>
              <li>Utiliser le Service à des fins illégales ou non autorisées</li>
              <li>Tenter d'accéder de manière non autorisée au Service ou aux systèmes connexes</li>
              <li>Interférer avec le fonctionnement du Service</li>
              <li>Extraire, copier ou utiliser le contenu du Service de manière automatisée</li>
              <li>Violer les droits de propriété intellectuelle de tiers</li>
              <li>Télécharger du contenu illégal, offensant ou inapproprié</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Traitement des vidéos et responsabilité de l'utilisateur</h2>
            <h3>6.1 Nature du traitement - IMPORTANT</h3>
            <p>
              <strong>SmartBouffe NE STOCKE PAS les vidéos originales que vous soumettez.</strong> Le processus fonctionne comme suit :
            </p>
            <ul>
              <li>Vous fournissez un lien vers une vidéo hébergée ailleurs (YouTube, Facebook, TikTok, etc.)</li>
              <li>Notre système télécharge TEMPORAIREMENT la vidéo pour analyse par IA</li>
              <li>L'intelligence artificielle transcrit l'audio et génère une NOUVELLE recette textuelle</li>
              <li>La vidéo et l'audio originaux sont IMMÉDIATEMENT SUPPRIMÉS après le traitement</li>
              <li>Seule la recette textuelle GÉNÉRÉE PAR L'IA est conservée dans votre compte</li>
            </ul>
            <p>
              <strong>IMPORTANT :</strong> La recette générée est une INTERPRÉTATION créée par intelligence artificielle. 
              Ce n'est PAS une copie de la vidéo originale, mais une NOUVELLE ŒUVRE DÉRIVÉE produite par notre IA 
              basée sur l'analyse du contenu. SmartBouffe ne conserve, ne redistribue et ne revendique AUCUN droit 
              sur les vidéos sources originales.
            </p>
            
            <h3>6.2 Responsabilité de l'utilisateur pour les vidéos sources</h3>
            <p>
              <strong>EN SOUMETTANT UN LIEN VIDÉO, VOUS RECONNAISSEZ ET ACCEPTEZ QUE :</strong>
            </p>
            <ul>
              <li>Vous êtes SEUL RESPONSABLE de vous assurer que vous avez le droit d'utiliser la vidéo pour générer une recette</li>
              <li>Les recettes générées sont destinées à votre usage personnel uniquement, sauf autorisation du créateur original</li>
              <li>Vous NE DEVEZ PAS utiliser ce service pour violer les droits d'auteur de tiers</li>
              <li>SmartBouffe agit comme un simple outil technique de transformation et n'est PAS responsable de votre utilisation du contenu source</li>
              <li>Vous indemniserez et dégagerez SmartBouffe de toute responsabilité en cas de réclamation pour violation de droits d'auteur résultant de votre soumission de vidéos</li>
            </ul>
            
            <h3>6.3 Utilisation équitable et transformation</h3>
            <p>
              L'extraction de recettes textuelles à partir de vidéos par intelligence artificielle constitue une 
              TRANSFORMATION substantielle du contenu original. Les recettes textuelles générées sont des œuvres 
              nouvelles et distinctes.
            </p>
            <p>
              <strong>Note importante concernant le droit canadien :</strong> Au Canada, la notion d'« utilisation équitable » 
              (fair dealing) est plus restrictive que le « fair use » américain et s'applique principalement aux fins de 
              recherche, d'étude privée, d'éducation, de parodie, de satire, de critique ou de compte rendu d'actualité.
            </p>
            <p>
              <strong>Notre meilleure protection juridique repose sur le principe de TRANSFORMATION SUBSTANTIELLE :</strong> 
              L'IA crée une œuvre textuelle nouvelle à partir d'un contenu audiovisuel, ce qui constitue une transformation 
              créative du format et de la nature de l'œuvre. Néanmoins, cette protection n'est pas absolue et peut varier 
              selon les circonstances.
            </p>
            <p>
              <strong>Vous devez faire preuve de jugement et consulter un avocat si vous avez des questions sur vos droits.</strong>
            </p>
            
            <h3>6.4 Respect des plateformes tierces</h3>
            <p>
              SmartBouffe s'engage à respecter les fichiers robots.txt des plateformes tierces (YouTube, TikTok, Facebook, 
              Instagram, etc.) dans la mesure du possible. Notre service agit comme un outil destiné à l'usage personnel 
              de l'utilisateur.
            </p>
            <p>
              <strong>Marques de commerce :</strong> Les logos, noms et marques des plateformes tierces (YouTube™, TikTok®, 
              Facebook®, Instagram®, etc.) appartiennent à leurs propriétaires respectifs. SmartBouffe n'est PAS affiliée, 
              sponsorisée ou approuvée par ces plateformes. L'utilisation de ces noms est à des fins d'identification uniquement.
            </p>
            
            <h3>6.5 Contenu que vous créez</h3>
            <p>
              Vous conservez la propriété des recettes textuelles générées et stockées dans votre compte. Ces recettes 
              sont considérées comme votre contenu personnel. En utilisant le Service, vous nous accordez une licence 
              mondiale, non exclusive et libre de redevances pour héberger, stocker et afficher ces recettes générées 
              dans le cadre de la fourniture du Service.
            </p>
            
            <h3>6.6 Clause d'indemnisation</h3>
            <p>
              <strong>SmartBouffe décline expressément toute responsabilité concernant :</strong>
            </p>
            <ul>
              <li>Les violations de droits d'auteur résultant de votre soumission de liens vidéo</li>
              <li>L'utilisation que vous faites des recettes générées</li>
              <li>Toute réclamation de tiers concernant le contenu des vidéos sources</li>
              <li>Les litiges entre vous et les créateurs de contenu original</li>
            </ul>
            <p>
              <strong>EN UTILISANT CE SERVICE, VOUS ACCEPTEZ D'INDEMNISER ET DE DÉGAGER SMARTBOUFFE, SES DIRIGEANTS, 
              EMPLOYÉS ET PARTENAIRES DE TOUTE RÉCLAMATION, POURSUITE, DOMMAGE OU DÉPENSE (Y COMPRIS LES FRAIS D'AVOCAT) 
              DÉCOULANT DE VOTRE VIOLATION DES DROITS D'AUTEUR DE TIERS.</strong>
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Propriété intellectuelle</h2>
            <p>
              Le Service, y compris tous les logiciels, designs, textes, graphiques, logos et autres matériaux, est
              la propriété de SmartBouffe et est protégé par les lois sur la propriété intellectuelle. Vous ne pouvez
              pas copier, modifier, distribuer ou créer des œuvres dérivées du Service sans notre autorisation
              écrite.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Intelligence artificielle et contenu généré</h2>
            <p>
              Le Service utilise des technologies d'intelligence artificielle pour extraire et générer des recettes à
              partir de vidéos et de photos.
            </p>
            <h3>8.1 Limitations de l'IA</h3>
            <p>
              Bien que nous nous efforcions d'assurer l'exactitude, <strong>nous ne pouvons garantir que les recettes 
              générées seront toujours parfaites, complètes ou sans erreurs</strong>. L'IA peut :
            </p>
            <ul>
              <li>Mal interpréter des ingrédients ou des quantités</li>
              <li>Omettre certaines étapes ou ingrédients mentionnés dans la vidéo originale</li>
              <li>Suggérer des substitutions inappropriées pour certains régimes alimentaires</li>
              <li>Faire des erreurs de conversion d'unités de mesure</li>
            </ul>
            <h3>8.2 Responsabilité de l'utilisateur</h3>
            <p>
              <strong>L'Utilisateur a la responsabilité EXCLUSIVE de :</strong>
            </p>
            <ul>
              <li>Vérifier l'exactitude de TOUS les ingrédients et quantités avant toute préparation</li>
              <li>Exercer son propre jugement culinaire et professionnel</li>
              <li>Adapter les recettes selon ses connaissances et son expérience</li>
              <li>Consulter des sources additionnelles en cas de doute</li>
            </ul>
          </section>

          <section className="legal-section" style={{ backgroundColor: '#FFF3E0', padding: '1.5rem', borderRadius: '8px', border: '2px solid #FF9800' }}>
            <h2>9. ⚠️ AVERTISSEMENT SUR LA SÉCURITÉ ALIMENTAIRE ET LES ALLERGÈNES</h2>
            <p style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#E65100' }}>
              IMPORTANT : Les recettes générées par SmartBouffe sont le résultat d'un traitement automatisé par 
              intelligence artificielle et PEUVENT CONTENIR DES ERREURS.
            </p>
            
            <h3>9.1 Vérification obligatoire</h3>
            <p>
              <strong>L'Utilisateur a la responsabilité EXCLUSIVE de vérifier l'exactitude des :</strong>
            </p>
            <ul>
              <li>Ingrédients (noms, nature, fraîcheur)</li>
              <li>Quantités et proportions</li>
              <li>Températures de cuisson</li>
              <li>Temps de cuisson et de préparation</li>
              <li>Techniques de manipulation des aliments</li>
            </ul>
            
            <h3>9.2 Allergies et restrictions alimentaires</h3>
            <p>
              <strong style={{ color: '#D32F2F' }}>MISE EN GARDE CRITIQUE :</strong> L'IA peut :
            </p>
            <ul>
              <li><strong>OMETTRE</strong> de signaler des allergènes présents dans la vidéo originale</li>
              <li><strong>NE PAS DÉTECTER</strong> certains allergènes cachés ou dérivés</li>
              <li><strong>SUGGÉRER</strong> des substitutions inappropriées pour votre condition</li>
              <li><strong>MAL IDENTIFIER</strong> certains ingrédients visuellement similaires</li>
            </ul>
            <p>
              <strong>Si vous avez des allergies alimentaires, des intolérances ou des restrictions médicales, 
              vous DEVEZ inspecter SCRUPULEUSEMENT chaque recette et chaque ingrédient avant toute préparation 
              ou consommation.</strong>
            </p>
            
            <h3>9.3 Risques sanitaires</h3>
            <p>
              <strong>SmartBouffe NE POURRA EN AUCUN CAS être tenu responsable de :</strong>
            </p>
            <ul>
              <li>Toute intoxication alimentaire</li>
              <li>Toute réaction allergique (légère ou sévère, incluant l'anaphylaxie)</li>
              <li>Toute blessure survenue lors de la préparation</li>
              <li>Tout dommage matériel ou corporel résultant de l'utilisation des recettes générées</li>
            </ul>
            <p>
              <strong>L'Utilisateur reconnaît que la cuisine comporte des risques intrinsèques (brûlures, coupures, 
              allergies, intoxications) et qu'il exerce cette activité À SES PROPRES RISQUES ET PÉRILS.</strong>
            </p>
            
            <h3>9.4 Recommandations de sécurité</h3>
            <p>Nous recommandons fortement de :</p>
            <ul>
              <li>Toujours vérifier la fraîcheur et la qualité des ingrédients utilisés</li>
              <li>Respecter les règles d'hygiène alimentaire de base</li>
              <li>Utiliser un thermomètre alimentaire pour vérifier les températures de cuisson</li>
              <li>Consulter un professionnel de la santé en cas de doute sur les allergènes</li>
              <li>Ne jamais servir de la nourriture à des personnes allergiques sans certitude absolue sur les ingrédients</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>10. Limitation de responsabilité générale</h2>
            <p>
              LE SERVICE EST FOURNI « TEL QUEL » ET « SELON DISPONIBILITÉ ». NOUS NE GARANTISSONS PAS QUE LE SERVICE
              SERA ININTERROMPU, SÉCURISÉ OU EXEMPT D'ERREURS. DANS LES LIMITES PERMISES PAR LA LOI, NOUS DÉCLINONS
              TOUTE GARANTIE, EXPRESSE OU IMPLICITE, Y COMPRIS LES GARANTIES DE QUALITÉ MARCHANDE, D'ADÉQUATION À UN
              USAGE PARTICULIER ET DE NON-CONTREFAÇON.
            </p>
            <p>
              EN AUCUN CAS NOUS NE SERONS RESPONSABLES DE DOMMAGES INDIRECTS, ACCESSOIRES, SPÉCIAUX, CONSÉCUTIFS OU
              PUNITIFS, OU DE PERTE DE PROFITS, REVENUS, DONNÉES OU UTILISATION, QUE CE SOIT DANS UNE ACTION
              CONTRACTUELLE, DÉLICTUELLE OU AUTRE, DÉCOULANT DE VOTRE UTILISATION DU SERVICE.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Résiliation</h2>
            <p>
              Nous nous réservons le droit de suspendre ou de résilier votre compte à tout moment, avec ou sans
              préavis, si nous estimons que vous avez violé ces Conditions d'utilisation. Vous pouvez également
              résilier votre compte à tout moment en nous contactant.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Modifications des conditions</h2>
            <p>
              Nous nous réservons le droit de modifier ces Conditions d'utilisation à tout moment. Les modifications
              entreront en vigueur dès leur publication sur le Service. Votre utilisation continue du Service après
              la publication de modifications constitue votre acceptation de ces modifications.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Loi applicable et juridiction</h2>
            <p>
              Ces Conditions d'utilisation sont régies par les lois de la province de Québec et les lois fédérales du
              Canada applicables, y compris mais sans s'y limiter : la Loi sur le droit d'auteur (L.R.C. (1985), ch. C-42), 
              la Loi sur la protection des renseignements personnels et les documents électroniques (LPRPDE), et la 
              Loi sur la protection du consommateur du Québec.
            </p>
            <p>
              Tout litige découlant de ces conditions sera soumis à la juridiction exclusive des tribunaux du 
              district judiciaire de Montréal, province de Québec, Canada.
            </p>
          </section>

          <section className="legal-section">
            <h2>14. Contact</h2>
            <p>
              Pour toute question concernant ces Conditions d'utilisation, veuillez nous contacter à :{' '}
              <a href="mailto:support@smartbouffe.com">support@smartbouffe.com</a>
            </p>
          </section>
        </>
      ),
    },

    privacy: {
      title: 'Politique de confidentialité',
      lastUpdated: 'Dernière mise à jour : Janvier 2025',
      content: (
        <>
          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              SmartBouffe (« nous », « notre », « nos ») s'engage à protéger votre vie privée. Cette Politique de
              confidentialité explique comment nous collectons, utilisons, divulguons et protégeons vos informations
              personnelles lorsque vous utilisez notre Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Informations que nous collectons</h2>
            <h3>2.1 Informations fournies par l'utilisateur</h3>
            <ul>
              <li>Nom et adresse courriel lors de l'inscription</li>
              <li>Préférences alimentaires et allergies</li>
              <li>Contenu de recettes créées ou téléchargées (vidéos, photos, textes)</li>
              <li>Informations de profil utilisateur</li>
            </ul>
            <h3>2.2 Informations collectées automatiquement</h3>
            <ul>
              <li>Données d'utilisation du Service (pages visitées, fonctionnalités utilisées)</li>
              <li>Informations techniques (adresse IP, type de navigateur, système d'exploitation)</li>
              <li>Cookies et technologies similaires</li>
              <li>Logs d'erreurs et de performance</li>
            </ul>
            <h3>2.3 Informations de tiers</h3>
            <ul>
              <li>Si vous vous connectez via Google OAuth, nous recevons votre nom et adresse courriel de Google</li>
              <li>Informations de transaction de PayPal lors de l'achat de crédits</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Utilisation de vos informations</h2>
            <p>Nous utilisons vos informations pour :</p>
            <ul>
              <li>Fournir, maintenir et améliorer le Service</li>
              <li>Créer et gérer votre compte utilisateur</li>
              <li>Traiter les extractions de recettes via l'intelligence artificielle</li>
              <li>Personnaliser les recettes selon vos préférences alimentaires et allergies</li>
              <li>Traiter vos paiements et gérer vos crédits</li>
              <li>Vous envoyer des notifications importantes concernant le Service</li>
              <li>Analyser l'utilisation du Service pour améliorer nos fonctionnalités</li>
              <li>Détecter, prévenir et résoudre les problèmes techniques et de sécurité</li>
              <li>Respecter nos obligations légales</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Intelligence artificielle et traitement des données</h2>
            <h3>4.1 Traitement des vidéos et photos</h3>
            <p>
              Lorsque vous soumettez une vidéo ou une photo pour extraction de recette, nous utilisons les
              technologies suivantes :
            </p>
            <ul>
              <li>Whisper d'OpenAI pour la transcription audio</li>
              <li>Gemini Vision de Google pour l'analyse d'images</li>
              <li>Modèles de langage pour générer des recettes structurées</li>
            </ul>
            <h3>4.2 Conservation des données</h3>
            <p>
              Les vidéos et photos que vous soumettez sont traitées temporairement pour l'extraction, puis
              supprimées. Seules les recettes textuelles générées et, dans le cas des photos, les images sources
              compressées sont conservées dans votre compte.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Partage de vos informations</h2>
            <p>Nous ne vendons pas vos informations personnelles. Nous pouvons partager vos informations avec :</p>
            <h3>5.1 Fournisseurs de services</h3>
            <ul>
              <li>OpenAI (pour la transcription audio via Whisper)</li>
              <li>Google (pour l'authentification OAuth et l'analyse d'images via Gemini Vision)</li>
              <li>PayPal (pour le traitement des paiements)</li>
              <li>Services d'hébergement et d'infrastructure cloud</li>
            </ul>
            <h3>5.2 Conformité légale</h3>
            <p>
              Nous pouvons divulguer vos informations si la loi l'exige ou en réponse à des procédures judiciaires
              valides.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Sécurité des données</h2>
            <p>
              Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour
              protéger vos informations personnelles contre l'accès non autorisé, la perte, la destruction ou
              l'altération. Ces mesures incluent :
            </p>
            <ul>
              <li>Chiffrement des données sensibles</li>
              <li>Authentification sécurisée avec hachage des mots de passe (bcrypt)</li>
              <li>Connexions HTTPS sécurisées</li>
              <li>Contrôles d'accès stricts aux systèmes</li>
              <li>Surveillance et journalisation des activités</li>
            </ul>
            <p>
              Cependant, aucune méthode de transmission sur Internet ou de stockage électronique n'est totalement
              sécurisée. Nous ne pouvons garantir la sécurité absolue de vos informations.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Conservation des données</h2>
            <p>
              Nous conservons vos informations personnelles aussi longtemps que nécessaire pour fournir le Service et
              respecter nos obligations légales. Les recettes et le contenu que vous créez sont conservés jusqu'à ce
              que vous supprimiez votre compte ou demandiez leur suppression.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Vos droits</h2>
            <p>Conformément aux lois applicables, vous avez les droits suivants :</p>
            <ul>
              <li>
                <strong>Droit d'accès :</strong> Vous pouvez demander une copie de vos informations personnelles
              </li>
              <li>
                <strong>Droit de rectification :</strong> Vous pouvez corriger vos informations inexactes ou
                incomplètes
              </li>
              <li>
                <strong>Droit de suppression :</strong> Vous pouvez demander la suppression de vos informations
                personnelles
              </li>
              <li>
                <strong>Droit à la portabilité :</strong> Vous pouvez demander une copie de vos données dans un
                format structuré
              </li>
              <li>
                <strong>Droit d'opposition :</strong> Vous pouvez vous opposer au traitement de vos informations dans
                certaines circonstances
              </li>
            </ul>
            <p>
              Pour exercer ces droits, veuillez nous contacter à{' '}
              <a href="mailto:privacy@smartbouffe.com">privacy@smartbouffe.com</a>
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Cookies et technologies de suivi</h2>
            <p>
              Nous utilisons des cookies et des technologies similaires pour améliorer votre expérience, analyser
              l'utilisation du Service et mémoriser vos préférences. Vous pouvez contrôler les cookies via les
              paramètres de votre navigateur, mais cela peut affecter certaines fonctionnalités du Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Services tiers</h2>
            <p>
              Le Service peut contenir des liens vers des sites web ou des services tiers. Nous ne sommes pas
              responsables des pratiques de confidentialité de ces tiers. Nous vous encourageons à lire les
              politiques de confidentialité de tout site web ou service tiers que vous visitez.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Protection des mineurs</h2>
            <p>
              Le Service n'est pas destiné aux personnes de moins de 13 ans. Nous ne collectons pas sciemment
              d'informations personnelles auprès de mineurs de moins de 13 ans. Si nous apprenons que nous avons
              collecté des informations d'un mineur, nous prendrons des mesures pour supprimer ces informations.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Modifications de cette politique</h2>
            <p>
              Nous pouvons mettre à jour cette Politique de confidentialité de temps à autre. Nous vous informerons
              de tout changement important en publiant la nouvelle politique sur cette page et en mettant à jour la
              date de « Dernière mise à jour ». Votre utilisation continue du Service après la publication de
              modifications constitue votre acceptation de ces modifications.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Contact</h2>
            <p>
              Pour toute question concernant cette Politique de confidentialité ou pour exercer vos droits, veuillez
              nous contacter à :
            </p>
            <p>
              Courriel : <a href="mailto:privacy@smartbouffe.com">privacy@smartbouffe.com</a>
            </p>
          </section>
        </>
      ),
    },

    copyright: {
      title: "Avis de droits d'auteur",
      lastUpdated: "Dernière mise à jour : Janvier 2025",
      content: (
        <>
          <section className="legal-section">
            <h2>1. Droits d'auteur de SmartBouffe</h2>
            <p>
              © 2025 SmartBouffe. Tous droits réservés. Le Service, y compris tous les logiciels, designs, textes,
              graphiques, logos, icônes, images, clips audio et vidéo, téléchargements numériques, compilations de
              données et autres matériaux, est la propriété de SmartBouffe ou de ses concédants de licence et est
              protégé par les lois canadiennes et internationales sur le droit d'auteur.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Licence d'utilisation limitée</h2>
            <p>
              Sous réserve de votre conformité aux Conditions d'utilisation, nous vous accordons une licence limitée,
              non exclusive, non transférable et révocable pour accéder et utiliser le Service à des fins
              personnelles et non commerciales. Cette licence ne vous autorise pas à :
            </p>
            <ul>
              <li>Copier, modifier ou distribuer le Service ou son contenu</li>
              <li>Créer des œuvres dérivées basées sur le Service</li>
              <li>Décompiler, désassembler ou effectuer de l'ingénierie inverse sur le Service</li>
              <li>Retirer ou modifier les avis de propriété intellectuelle</li>
              <li>Utiliser le Service à des fins commerciales sans notre autorisation écrite</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Contenu généré par l'utilisateur</h2>
            <h3>3.1 Vos droits</h3>
            <p>
              Vous conservez tous les droits de propriété sur le contenu que vous créez ou téléchargez sur
              SmartBouffe, y compris les recettes, photos et vidéos. En utilisant le Service, vous nous accordez une
              licence mondiale, non exclusive, libre de redevances, transférable et pouvant faire l'objet de
              sous-licences pour utiliser, reproduire, distribuer, préparer des œuvres dérivées, afficher et exécuter
              votre contenu dans le cadre de la fourniture du Service.
            </p>
            <h3>3.2 Responsabilité</h3>
            <p>
              Vous déclarez et garantissez que vous possédez ou avez obtenu tous les droits, licences, consentements
              et autorisations nécessaires pour télécharger et partager votre contenu sur le Service. Vous êtes seul
              responsable de tout contenu que vous soumettez.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Nature du service et traitement des vidéos sources</h2>
            <h3>4.1 Service de transformation par IA - Non conservation des vidéos</h3>
            <p>
              <strong>CLAUSE IMPORTANTE DE PROTECTION :</strong>
            </p>
            <p>
              SmartBouffe est un service de TRANSFORMATION de contenu par intelligence artificielle. Le Service fonctionne comme suit :
            </p>
            <ul>
              <li>
                <strong>AUCUNE CONSERVATION DES VIDÉOS :</strong> SmartBouffe ne stocke, n'héberge, ne conserve ou ne redistribue 
                JAMAIS les vidéos sources que vous soumettez
              </li>
              <li>
                <strong>TRAITEMENT TEMPORAIRE UNIQUEMENT :</strong> Les vidéos sont téléchargées temporairement (quelques secondes 
                à quelques minutes) uniquement pour permettre à l'IA d'analyser le contenu audio et visuel
              </li>
              <li>
                <strong>SUPPRESSION IMMÉDIATE :</strong> Dès que l'IA a terminé l'analyse, les fichiers vidéo et audio sont 
                automatiquement et définitivement supprimés de nos serveurs
              </li>
              <li>
                <strong>CRÉATION D'ŒUVRE NOUVELLE :</strong> L'IA génère une NOUVELLE recette textuelle qui est une interprétation 
                et transformation du contenu original, pas une copie
              </li>
              <li>
                <strong>SEUL LE TEXTE EST CONSERVÉ :</strong> Seule la recette textuelle générée par l'IA est sauvegardée dans 
                votre compte personnel
              </li>
            </ul>
            
            <h3>4.2 Transformation substantielle et œuvre dérivée</h3>
            <p>
              Les recettes textuelles générées par SmartBouffe constituent des <strong>ŒUVRES TRANSFORMÉES</strong> et non des 
              copies du contenu original. Cette transformation implique :
            </p>
            <ul>
              <li>Transcription audio par intelligence artificielle</li>
              <li>Analyse et compréhension du contenu par IA</li>
              <li>Reformulation et restructuration des instructions</li>
              <li>Conversion des mesures (métrique/québécois)</li>
              <li>Ajout de métadonnées (temps, température, portions)</li>
              <li>Classification diététique et détection d'allergènes</li>
            </ul>
            <p>
              Le résultat final est une <strong>ŒUVRE NOUVELLE ET DISTINCTE</strong> créée par notre IA, qui diffère 
              substantiellement du contenu vidéo original tant dans sa forme que dans sa présentation.
            </p>
            
            <h3>4.3 Responsabilité de l'utilisateur - Déclaration importante</h3>
            <p>
              <strong>EN UTILISANT CE SERVICE, VOUS RECONNAISSEZ ET ACCEPTEZ QUE :</strong>
            </p>
            <ul>
              <li>
                <strong>VOUS êtes seul responsable</strong> de vous assurer que vous avez le droit d'utiliser les vidéos 
                que vous soumettez pour générer des recettes
              </li>
              <li>
                Les recettes générées sont destinées à <strong>votre usage personnel et privé</strong> uniquement, sauf 
                autorisation expresse du créateur original
              </li>
              <li>
                Vous ne devez pas partager publiquement ou utiliser commercialement des recettes extraites sans permission
              </li>
              <li>
                <strong>SmartBouffe agit uniquement comme outil technique</strong> de transformation et n'est PAS responsable 
                de l'utilisation que vous faites des vidéos sources
              </li>
              <li>
                Vous <strong>INDEMNISEREZ et DÉGAGEREZ SmartBouffe</strong> de toute responsabilité, réclamation, poursuite, 
                dommage ou frais (incluant les honoraires d'avocat) résultant de votre violation de droits d'auteur ou de 
                propriété intellectuelle de tiers
              </li>
            </ul>
            
            <h3>4.4 Absence de propriété sur les vidéos sources</h3>
            <p>
              <strong>SMARTBOUFFE NE REVENDIQUE AUCUN DROIT</strong> sur les vidéos sources que vous soumettez. En fournissant 
              un lien vers une vidéo :
            </p>
            <ul>
              <li>Vous ne transférez AUCUN droit de propriété à SmartBouffe</li>
              <li>SmartBouffe ne devient pas propriétaire de la vidéo</li>
              <li>SmartBouffe n'acquiert aucune licence sur la vidéo originale</li>
              <li>La vidéo reste la propriété exclusive de son créateur original</li>
              <li>SmartBouffe se limite à fournir un service technique de transformation par IA</li>
            </ul>
            
            <h3>4.5 Utilisation équitable (Fair Use / Fair Dealing)</h3>
            <p>
              L'utilisation de SmartBouffe peut être considérée comme relevant de l'<strong>utilisation équitable</strong> 
              (fair dealing) selon les lois canadiennes sur le droit d'auteur, particulièrement lorsque :
            </p>
            <ul>
              <li>L'utilisation est à des fins privées et personnelles</li>
              <li>Il y a une transformation substantielle du contenu original</li>
              <li>L'œuvre dérivée a un caractère et un objectif différents de l'original</li>
              <li>Aucune copie de l'œuvre originale n'est conservée ou distribuée</li>
            </ul>
            <p>
              Cependant, <strong>cette détermination dépend de multiples facteurs</strong> et peut varier selon les 
              circonstances spécifiques. Vous devez faire preuve de jugement et consulter un avocat spécialisé en 
              propriété intellectuelle si vous avez des questions sur vos droits.
            </p>
            
            <h3>4.6 Limitation expresse de responsabilité de SmartBouffe</h3>
            <p>
              <strong>SMARTBOUFFE DÉCLINE EXPRESSÉMENT ET CATÉGORIQUEMENT TOUTE RESPONSABILITÉ CONCERNANT :</strong>
            </p>
            <ul>
              <li>Les violations de droits d'auteur résultant de votre soumission de liens vidéo</li>
              <li>L'origine, l'authenticité ou la légalité des vidéos que vous soumettez</li>
              <li>L'utilisation que vous faites des recettes générées par l'IA</li>
              <li>Toute réclamation, poursuite ou action en justice de tiers concernant les vidéos sources</li>
              <li>Les litiges entre vous et les créateurs de contenu original</li>
              <li>Les conséquences de votre utilisation non autorisée de contenu protégé par droits d'auteur</li>
            </ul>
            <p>
              <strong>CLAUSE D'INDEMNISATION :</strong> En utilisant ce Service, vous acceptez d'indemniser, défendre et 
              dégager SmartBouffe, ses dirigeants, administrateurs, employés, agents, partenaires et concédants de licence 
              de toute réclamation, poursuite, responsabilité, dommage, perte, coût ou dépense (y compris, sans limitation, 
              les honoraires d'avocat raisonnables) découlant de ou liée à votre violation des droits de propriété 
              intellectuelle de tiers ou de toute loi applicable.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Technologie d'intelligence artificielle</h2>
            <p>
              Les recettes générées par SmartBouffe utilisent des technologies d'intelligence artificielle, y compris
              des modèles de langage et d'analyse d'images. Bien que ces technologies produisent du contenu original,
              elles sont formées sur de vastes ensembles de données qui peuvent inclure du contenu protégé par le
              droit d'auteur. SmartBouffe s'efforce de respecter tous les droits de propriété intellectuelle et de
              générer du contenu original.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Marques de commerce</h2>
            <p>
              « SmartBouffe » et tous les logos, noms de produits et services associés sont des marques de commerce
              de SmartBouffe. Vous ne pouvez pas utiliser ces marques sans notre autorisation écrite préalable.
              Toutes les autres marques mentionnées sur le Service appartiennent à leurs propriétaires respectifs.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Notification de violation de droits d'auteur</h2>
            <p>
              Si vous pensez que votre œuvre protégée par le droit d'auteur a été copiée d'une manière qui constitue
              une violation de droits d'auteur et est accessible sur le Service, veuillez nous le signaler en
              fournissant les informations suivantes :
            </p>
            <ul>
              <li>Une signature électronique ou physique du propriétaire des droits d'auteur</li>
              <li>Une description de l'œuvre protégée que vous prétendez avoir été violée</li>
              <li>L'emplacement du contenu présumé contrefaisant sur le Service</li>
              <li>Vos coordonnées (adresse, numéro de téléphone, adresse courriel)</li>
              <li>
                Une déclaration selon laquelle vous croyez de bonne foi que l'utilisation contestée n'est pas
                autorisée
              </li>
              <li>
                Une déclaration selon laquelle les informations contenues dans votre avis sont exactes et que vous
                êtes autorisé à agir au nom du propriétaire des droits d'auteur
              </li>
            </ul>
            <p>
              Envoyez votre notification à : <a href="mailto:copyright@smartbouffe.com">copyright@smartbouffe.com</a>
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Contre-notification</h2>
            <p>
              Si vous pensez que votre contenu a été supprimé par erreur ou par identification erronée, vous pouvez
              envoyer une contre-notification contenant les informations suivantes :
            </p>
            <ul>
              <li>Votre signature électronique ou physique</li>
              <li>Une description du contenu qui a été supprimé</li>
              <li>Une déclaration selon laquelle vous croyez de bonne foi que le contenu a été supprimé par erreur</li>
              <li>Vos nom, adresse, numéro de téléphone et adresse courriel</li>
              <li>
                Une déclaration selon laquelle vous consentez à la juridiction du tribunal fédéral du district où
                vous résidez
              </li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>9. Politique en matière de violations répétées</h2>
            <p>
              Conformément à la loi canadienne sur le droit d'auteur, nous avons adopté une politique prévoyant la
              résiliation, dans les circonstances appropriées, des comptes des utilisateurs qui sont considérés comme
              des contrevenants répétés.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Contact</h2>
            <p>
              Pour toute question concernant les droits d'auteur, veuillez nous contacter à :{' '}
              <a href="mailto:copyright@smartbouffe.com">copyright@smartbouffe.com</a>
            </p>
          </section>
        </>
      ),
    },

    'legal-notice': {
      title: 'Mentions légales',
      lastUpdated: 'Dernière mise à jour : Janvier 2025',
      content: (
        <>
          <section className="legal-section">
            <h2>1. Éditeur du site</h2>
            <p>
              <strong>Nom du service :</strong> SmartBouffe
              <br />
              <strong>Nature de l'activité :</strong> Application web de conversion de vidéos et photos de recettes
              en recettes textuelles via intelligence artificielle
              <br />
              <strong>Courriel :</strong>{' '}
              <a href="mailto:contact@smartbouffe.com">contact@smartbouffe.com</a>
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Hébergement</h2>
            <p>
              Le Service est hébergé sur une infrastructure cloud sécurisée utilisant des services d'hébergement
              professionnels conformes aux normes de l'industrie. Les données sont stockées et traitées de manière
              sécurisée conformément à notre Politique de confidentialité.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. Propriété intellectuelle</h2>
            <p>
              L'ensemble du contenu présent sur SmartBouffe, incluant, de manière non limitative, les textes,
              images, graphismes, logos, icônes, sons, logiciels, est la propriété exclusive de SmartBouffe ou de ses
              partenaires, et est protégé par les lois canadiennes et internationales relatives à la propriété
              intellectuelle.
            </p>
            <p>
              Toute reproduction, représentation, modification, publication, adaptation de tout ou partie des éléments
              du site, quel que soit le moyen ou le procédé utilisé, est interdite, sauf autorisation écrite préalable
              de SmartBouffe.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Responsabilité et limitations</h2>
            <h3>4.1 Contenu du Service</h3>
            <p>
              SmartBouffe s'efforce d'assurer l'exactitude et la mise à jour des informations diffusées sur le
              Service. Toutefois, SmartBouffe ne peut garantir l'exactitude, la précision ou l'exhaustivité des
              informations mises à disposition sur le Service.
            </p>
            <h3>4.2 Recettes générées par l'IA</h3>
            <p>
              Les recettes générées par l'intelligence artificielle sont fournies à titre informatif uniquement.
              SmartBouffe ne peut être tenu responsable de l'exactitude, de la qualité ou de la sécurité alimentaire
              des recettes générées. Les utilisateurs sont invités à faire preuve de jugement et à vérifier les
              recettes avant de les préparer.
            </p>
            <h3>4.3 Allergies et restrictions alimentaires</h3>
            <p>
              Bien que le Service offre des fonctionnalités pour gérer les préférences alimentaires et les allergies,
              SmartBouffe ne peut garantir que toutes les recettes seront exemptes d'allergènes ou conformes à vos
              restrictions alimentaires. Il est de votre responsabilité de vérifier tous les ingrédients avant de
              préparer ou de consommer une recette.
            </p>
            <h3>4.4 Disponibilité du Service</h3>
            <p>
              SmartBouffe s'efforce de maintenir le Service accessible 24 heures sur 24, 7 jours sur 7. Toutefois,
              SmartBouffe se réserve le droit d'interrompre l'accès au Service pour des raisons de maintenance, de
              mise à jour ou pour toute autre raison technique. SmartBouffe ne peut être tenu responsable des
              interruptions de service et de leurs conséquences.
            </p>
            <h3>4.5 Traitement des vidéos et responsabilité pour droits d'auteur</h3>
            <p>
              <strong>LIMITATION IMPORTANTE DE RESPONSABILITÉ :</strong>
            </p>
            <p>
              SmartBouffe fournit un service technique de <strong>TRANSFORMATION PAR INTELLIGENCE ARTIFICIELLE</strong>. 
              Le Service NE CONSERVE PAS les vidéos sources et génère uniquement des recettes textuelles transformées.
            </p>
            <ul>
              <li>
                <strong>Aucune conservation :</strong> SmartBouffe ne stocke, n'héberge, ne conserve ou ne redistribue 
                les vidéos sources. Les fichiers sont téléchargés temporairement (quelques secondes) puis supprimés 
                immédiatement après traitement
              </li>
              <li>
                <strong>Œuvres transformées :</strong> Les recettes générées sont des œuvres nouvelles créées par IA, 
                distinctes des vidéos originales
              </li>
              <li>
                <strong>Responsabilité de l'utilisateur :</strong> VOUS êtes seul responsable de vous assurer que vous 
                avez le droit d'utiliser les vidéos que vous soumettez. SmartBouffe agit uniquement comme outil technique
              </li>
              <li>
                <strong>Absence de revendication :</strong> SmartBouffe ne revendique AUCUN droit sur les vidéos sources 
                et ne devient jamais propriétaire des vidéos soumises
              </li>
              <li>
                <strong>Clause d'indemnisation :</strong> L'utilisateur accepte d'indemniser et de dégager SmartBouffe de 
                toute réclamation pour violation de droits d'auteur résultant de sa soumission de vidéos
              </li>
            </ul>
            <p>
              <strong>SmartBouffe décline expressément toute responsabilité concernant :</strong>
            </p>
            <ul>
              <li>Les violations de droits d'auteur résultant de votre utilisation du Service</li>
              <li>L'origine, l'authenticité ou la légalité des vidéos que vous soumettez</li>
              <li>Toute réclamation de tiers concernant le contenu des vidéos sources</li>
              <li>Les litiges entre vous et les créateurs de contenu original</li>
              <li>Les conséquences juridiques de votre utilisation non autorisée de contenu protégé</li>
            </ul>
            <h3>4.6 Contenu tiers - Photos et images</h3>
            <p>
              SmartBouffe n'est pas responsable du contenu provenant de sources tierces (photos de magazines, livres, 
              sites web) que les utilisateurs soumettent pour extraction de recettes. Les utilisateurs sont seuls 
              responsables de s'assurer qu'ils ont les droits nécessaires pour utiliser ce contenu.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Données personnelles</h2>
            <p>
              SmartBouffe s'engage à protéger la vie privée de ses utilisateurs. La collecte, l'utilisation et le
              traitement de vos données personnelles sont régis par notre Politique de confidentialité. En utilisant
              le Service, vous consentez à cette collecte et à ce traitement conformément à la Politique de
              confidentialité.
            </p>
            <p>
              Conformément à la Loi sur la protection des renseignements personnels et les documents électroniques
              (LPRPDE) du Canada, vous disposez d'un droit d'accès, de rectification et de suppression de vos données
              personnelles. Pour exercer ces droits, veuillez nous contacter à{' '}
              <a href="mailto:privacy@smartbouffe.com">privacy@smartbouffe.com</a>
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Cookies et technologies de suivi</h2>
            <p>
              Le Service utilise des cookies et des technologies similaires pour améliorer l'expérience utilisateur,
              analyser l'utilisation du Service et mémoriser vos préférences. En utilisant le Service, vous consentez
              à l'utilisation de ces technologies conformément à notre Politique de confidentialité.
            </p>
            <p>
              Vous pouvez configurer votre navigateur pour refuser les cookies, mais cela peut affecter certaines
              fonctionnalités du Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Paiements et transactions</h2>
            <h3>7.1 Traitement des paiements</h3>
            <p>
              Les paiements pour les abonnements et vidéos recettes bonus sont traités de manière sécurisée via PayPal. SmartBouffe ne
              stocke pas vos informations de carte de crédit ou de paiement. Toutes les transactions sont soumises
              aux conditions générales de PayPal.
            </p>
            <h3>7.2 Prix et tarification</h3>
            <p>
              Les prix des abonnements et vidéos recettes bonus sont affichés en dollars canadiens (CAD) et incluent toutes les taxes applicables.
              SmartBouffe se réserve le droit de modifier les prix à tout moment, mais les modifications n'affecteront
              pas les abonnements en cours jusqu'à leur renouvellement.
            </p>
            <h3>7.3 Non-cumul des vidéos recettes</h3>
            <p>
              <strong>IMPORTANT :</strong> Les vidéos recettes des forfaits payants (Starter, Standard, Pro) ne sont pas cumulables d'un mois à l'autre. 
              Les vidéos recettes non utilisées sont perdues à chaque renouvellement mensuel. Seul le forfait Gratuit permet de cumuler les vidéos recettes.
              Les vidéos recettes bonus achetées séparément n'expirent pas.
            </p>
            <h3>7.4 Remboursements</h3>
            <p>
              Les abonnements et téléchargements bonus achetés ne sont généralement pas remboursables, sauf en cas de défaut du Service ou de
              circonstances exceptionnelles à la discrétion de SmartBouffe. Pour toute demande de remboursement,
              veuillez contacter notre service client.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Liens hypertextes</h2>
            <p>
              Le Service peut contenir des liens vers des sites web tiers. SmartBouffe n'exerce aucun contrôle sur
              ces sites et n'est pas responsable de leur contenu, de leurs politiques de confidentialité ou de leurs
              pratiques. L'inclusion de liens vers des sites tiers ne constitue pas une approbation de ces sites par
              SmartBouffe.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Loi applicable et juridiction</h2>
            <p>
              Les présentes mentions légales et l'utilisation du Service sont régies par les lois de la province de
              Québec et les lois fédérales du Canada applicables. Tout litige relatif à l'utilisation du Service sera
              soumis à la compétence exclusive des tribunaux du Québec.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Modification des mentions légales</h2>
            <p>
              SmartBouffe se réserve le droit de modifier les présentes mentions légales à tout moment. Les
              modifications entreront en vigueur dès leur publication sur cette page. Il est de votre responsabilité
              de consulter régulièrement cette page pour prendre connaissance des éventuelles modifications.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contact</h2>
            <p>Pour toute question concernant ces mentions légales, vous pouvez nous contacter :</p>
            <p>
              <strong>Par courriel :</strong>{' '}
              <a href="mailto:legal@smartbouffe.com">legal@smartbouffe.com</a>
              <br />
              <strong>Pour le service client :</strong>{' '}
              <a href="mailto:support@smartbouffe.com">support@smartbouffe.com</a>
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Crédits et attributions</h2>
            <p>Le Service SmartBouffe utilise les technologies suivantes :</p>
            <ul>
              <li>OpenAI Whisper pour la transcription audio</li>
              <li>Google Gemini Vision pour l'analyse d'images</li>
              <li>React pour l'interface utilisateur</li>
              <li>FastAPI pour le backend</li>
              <li>MongoDB pour le stockage de données</li>
              <li>PayPal pour le traitement des paiements</li>
            </ul>
            <p>
              Nous remercions les communautés open source et les fournisseurs de technologies qui rendent ce Service
              possible.
            </p>
          </section>
        </>
      ),
    },
  };

  const currentContent = legalContent[type] || legalContent.terms;

  return (
    <div className="legal-page">
      <div className="legal-header">
        <div className="legal-header-content">
          <Logo />
          <button onClick={() => navigate(-1)} className="back-button">
            <ArrowLeft size={20} />
            Retour
          </button>
        </div>
      </div>

      <div className="legal-container">
        <div className="legal-content">
          <h1 className="legal-title">{currentContent.title}</h1>
          <p className="legal-last-updated">{currentContent.lastUpdated}</p>
          {currentContent.content}
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
